import { useId, useState } from 'react';
import Card from '../../../../Components/Dashboard/Card';
import { CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import MiniChart from './MiniChart';
import styles from './analytics.module.css';

const TITLE = 'Analytics';
const RANGES = [
    ['30d', '30 days'],
    ['12m', '12 months'],
    ['5y', '5 years']
];
const sum = values => values.reduce((a, b) => a + b, 0);
const dateText = iso => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Admin-only aggregate counts. Every figure is a count from the server: no per-user data is requested or shown. */
const AnalyticsCards = () => {
    const [range, setRange] = useState('30d');
    const selectId = useId();
    const { status, data, error, reload } = useAsync(() => API.getAdminAnalytics({ range }).then(res => res.data), [range]);

    let body;
    if (status === 'loading' && !data) body = <CardLoading label={TITLE} />;
    else if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    else if (data) {
        const { buckets, granularity, registrations, signIns, testsRecorded, workouts } = data;
        const signInTotal = sum(signIns.counts);
        const rate = workouts.completionRate === null ? '—' : `${Math.round(workouts.completionRate * 100)}%`;
        body = (
            <div className={styles.grid} aria-busy={status === 'loading'}>
                <Card id="an-reg" module="admin-registrations" title="Registered users">
                    <p className={styles.big}>{registrations.total}</p>
                    <p className={styles.line}>{sum(registrations.newUsers)} new in this range</p>
                    <MiniChart title="Registered users (cumulative)" buckets={buckets} values={registrations.cumulative} granularity={granularity} kind="line" />
                    <p className={styles.note}>Cumulative registrations, not live active users.</p>
                </Card>
                <Card id="an-sign" module="admin-signins" title="Successful sign-ins">
                    <p className={styles.big}>{signInTotal}</p>
                    <p className={styles.line}>{signInTotal} in this range</p>
                    <MiniChart title="Successful sign-ins" buckets={buckets} values={signIns.counts} granularity={granularity} />
                    <p className={styles.note}>
                        {signIns.trackingStartedAt
                            ? `Sign-in event tracking started on ${dateText(signIns.trackingStartedAt)}.`
                            : 'Sign-in event tracking has not started yet.'}
                    </p>
                </Card>
                <Card id="an-tests" module="admin-tests" title="Tests recorded">
                    <p className={styles.big}>{testsRecorded.total}</p>
                    <p className={styles.line}>
                        {Object.entries(testsRecorded.byType)
                            .map(([type, n]) => `${type.replace(':', ' ')}: ${n}`)
                            .join(' · ')}
                    </p>
                    <MiniChart title="Tests recorded" buckets={buckets} values={testsRecorded.counts} granularity={granularity} />
                    <p className={styles.note}>Counted by the date the record was created.</p>
                </Card>
                <Card id="an-work" module="admin-workouts" title="Workouts">
                    <p className={styles.big}>{`${rate} completion rate`}</p>
                    <p className={styles.line}>{`Assigned ${workouts.assigned} · Completed ${workouts.completed} · Declined ${workouts.declined}`}</p>
                    <MiniChart title="Workouts completed" buckets={buckets} values={workouts.completedByPeriod} granularity={granularity} />
                    <p className={styles.note}>Completion rate is completed workouts out of all workouts.</p>
                </Card>
            </div>
        );
    }

    return (
        <section className={styles.section} aria-label={TITLE}>
            <div className={styles.head}>
                <h2 className={styles.title}>{TITLE}</h2>
                <label htmlFor={selectId} className={styles.rangeLabel}>
                    Range
                </label>
                <select id={selectId} value={range} onChange={e => setRange(e.target.value)} className={styles.range}>
                    {RANGES.map(([value, text]) => (
                        <option key={value} value={value}>
                            {text}
                        </option>
                    ))}
                </select>
            </div>
            {body}
        </section>
    );
};

export default AnalyticsCards;
